import React from 'react';
import {
    RibbonTopBar,
    type RibbonTabDefinitionBase,
    type RibbonTabDefinitionMLR,
    type RibbonTabDefinitionSLR,
    type RibbonTopBarProps,
    type TabHeader,
} from 'owa-acui';
import { appIcons, getAppStrings, getAppColors } from 'owa-acui-theme';
import { topBar, topBarContainer } from './CommandRibbonTopBar.scss';
import { observer } from 'owa-mobx-react';
import { safeRequestAnimationFrame } from 'owa-performance';

export interface CommandRibbonTopBarProps {
    extraControlsAfterTabs?: React.ReactNode;
    extraControlsBeforeTabs?: React.ReactNode;
    onTabHeaderClick: (tabId: string) => void;
    ribbonTabs: RibbonTabDefinitionBase[];
    selectedTab: RibbonTabDefinitionSLR | RibbonTabDefinitionMLR;
}

export const CommandRibbonTopBar: React.FunctionComponent<CommandRibbonTopBarProps> = observer(
    function CommandRibbonTopBar({
        extraControlsAfterTabs,
        extraControlsBeforeTabs,
        onTabHeaderClick,
        ribbonTabs,
        selectedTab,
    }: CommandRibbonTopBarProps) {
        // This is to ensure that dynamic indicator does not cause style recalcs and layout thrashing during initial render
        const [dynamicIndicator, setDynamicIndicator] = React.useState(false);

        React.useEffect(() => {
            safeRequestAnimationFrame(() => {
                setDynamicIndicator(true);
            });
        }, []);

        return (
            <div id="TopBar" className={topBar}>
                {extraControlsBeforeTabs}
                <div className={topBarContainer} onKeyDown={onKeyDown}>
                    <RibbonTopBar
                        appColors={getAppColors()}
                        appStrings={getAppStrings()}
                        customStyles={customTopBarStyles}
                        displayDynamicTabHeaderIndicator={dynamicIndicator}
                        isTabIndicatorRounded={true}
                        onTabHeaderClick={onTabHeaderClick}
                        overflowTabHeaderMenuLauncherIcon={appIcons.tabListOverflowLauncher}
                        ribbonTabs={customizeTabStyles(ribbonTabs)}
                        selectedTabId={selectedTab.id}
                    />
                </div>
                {extraControlsAfterTabs}
            </div>
        );
    },
    'CommandRibbonTopBar'
);

/** Removing the default background color to support the bleed-through themes */
const customTopBarStyles: RibbonTopBarProps['customStyles'] = {
    root: {
        background: 'none',
    },
};

/** Removing the default background color from each tab to support the bleed-through themes */
const customizeTabStyles = (tabs: RibbonTabDefinitionBase[]): RibbonTabDefinitionBase[] => {
    const styles: TabHeader['styles'] = {
        pivotHeader: {
            root: {
                background: 'none',
            },
        },
    };

    return tabs.map(tab => ({ ...tab, styles }));
};

const onKeyDown: React.KeyboardEventHandler = ({ key }) => {
    if (key === 'ArrowDown') {
        const firstBottomBarButton = document
            .getElementById('BottomBar')
            ?.querySelectorAll('button')[0];

        firstBottomBarButton?.focus();
    }
};
